import { useDispatch, useSelector } from 'react-redux';
import { customersAuthorizationApi } from '../api';
import { CookiesKey, EndpointsMvp } from './enum';
import { useAuth } from './useAuth';
import { EToastTypes } from '../../store/ducks/toast/types';
import { addToast } from '../../store/ducks/toast/actions';
import { deleteCookie, getValueCookie } from './utils';
import {
  CartItemPayload,
  CartProps,
  MinimunOrderItemProps,
  NewCartItem,
  SearchResponse
} from './models';
import { ApplicationState } from '../../store';
import { updateCartItemsQuantity } from '../../store/ducks/cart-items-mvp/actions';
import { ICustomer } from '../../store/ducks/customers/types';
import { useShoppingCart } from '../../providers/shoppingcart.provider';
interface customerTypeMvpProps extends ICustomer {
  customerType: string;
}

interface UnexpectedErrorProps {
  parameters?: {
    fieldName?: 'cartId';
  };
}

export interface ParamsOptionsProps {
  withPrice?: boolean;
  withOptions?: boolean;
}

export const UnexpectedError = (error: UnexpectedErrorProps) => {
  if (error.parameters?.fieldName === 'cartId') {
    deleteCookie('@cart');
  }

  throw Error();
};

export const useCartService = () => {
  const { updateProductsInformation } = useShoppingCart();
  const { getHeaders } = useAuth();
  const headers = getHeaders();

  const shoppingCart = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const customersState = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp.quantity
  );

  const dispatch = useDispatch();

  const handleUpdateQuantity = (qty: number) => {
    dispatch(updateCartItemsQuantity(qty));
  };

  const createNewCart = async (
    emitterId = '',
    receiverId = '',
    newCart = false,
    settingsOrderCheck = false
  ) => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId || emitterId,
      shipTo: shoppingCart.receiverId || receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);
    if (hasBodyKeyEmpty) {
      return null;
    }
    const existingCart = getCartId(emitterId);
    if (existingCart && existingCart.length > 0 && !newCart) {
      const cartQuantity = settingsOrderCheck
        ? await getCartItemQuantity({
            alreadyItems: undefined,
            countType: true
          })
        : await getCartItemQuantity();
      handleUpdateQuantity(cartQuantity);
      return {
        cartId: existingCart,
        alreadyCart: cartQuantity > 0,
        cartQuantity: cartQuantity ?? 0
      };
    }
    try {
      const response = await customersAuthorizationApi.post(
        `${EndpointsMvp.CREATE_CART}`,
        {
          ...bodyRequest
        },
        { headers }
      );

      deleteCookie('@cart');
      deleteCookie('@emitter');

      if (!isNaN(Number(response.data))) {
        document.cookie = `@cart=${response.data};  path=/`;
        document.cookie = `@emitter=${shoppingCart.emitterId};  path=/`;
        // setCookieWithExpiry({ name: '@ordercontinuos', value: true, hours: 2 });
      }

      handleUpdateQuantity(0);
      const cartId = response.data;

      return { cartId, alreadyCart: false, cartQuantity: 0 };
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao criar novo pedido',
          durationInMS: 3000
        })
      );
    }
  };

  const addCartItem = async (
    item: CartItemPayload,
    quantity?: number,
    paramsOptions?: ParamsOptionsProps
  ) => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const params = {
      includeOptions: paramsOptions?.withOptions
        ? paramsOptions?.withOptions
        : false,
      includePrices: paramsOptions?.withPrice ? paramsOptions?.withPrice : false
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);

    const cartId = getCartId();
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }

    const formatedItem = { ...item } as Partial<
      Pick<
        NewCartItem,
        | 'price'
        | 'item_id'
        | 'media_gallery_entries'
        | 'errors'
        | 'cartOptions'
        | 'sap_descricao_gpm'
        | 'descricao_gpm'
      >
    >;

    delete formatedItem.media_gallery_entries;
    delete formatedItem.errors;
    delete formatedItem.item_id;
    delete formatedItem.price;
    delete formatedItem.cartOptions;
    delete formatedItem.sap_descricao_gpm;
    delete formatedItem.descricao_gpm;

    try {
      const response = await customersAuthorizationApi.post(
        `${EndpointsMvp.ADD_ITEM(cartId)}`,
        { ...bodyRequest, cartItem: formatedItem },
        { headers, params }
      );

      if (response.data?.error) {
        if (
          ['The cart was already closed', 'No such entity'].includes(
            response.data.error?.message ?? ''
          )
        ) {
          // if error has cart expiration
          handleUpdateQuantity(0);
          const data = await recursiveAddCartItem(
            item,
            quantity,
            paramsOptions
          );
          return data;
        }
        UnexpectedError(response.data.error);
      }

      if (quantity && quantity >= 0) {
        handleUpdateQuantity(cartQuantity + 1);
      } else {
        const cartQuantity = await getCartItemQuantity();
        handleUpdateQuantity(cartQuantity);
      }

      return response.data;
    } catch (error) {
      console.error(error);
    }
  };

  const recursiveAddCartItem = async (
    item: CartItemPayload,
    quantity?: number,
    paramsOptions?: ParamsOptionsProps
  ) => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const params = {
      includeOptions: paramsOptions?.withOptions
        ? paramsOptions?.withOptions
        : false,
      includePrices: paramsOptions?.withPrice ? paramsOptions?.withPrice : false
    };

    const cartId = await createNewCart('', '', true);

    const formatedItem = { ...item } as Partial<
      Pick<
        NewCartItem,
        | 'price'
        | 'item_id'
        | 'media_gallery_entries'
        | 'errors'
        | 'cartOptions'
        | 'sap_descricao_gpm'
        | 'descricao_gpm'
      >
    >;

    delete formatedItem.media_gallery_entries;
    delete formatedItem.errors;
    delete formatedItem.item_id;
    delete formatedItem.price;
    delete formatedItem.cartOptions;
    delete formatedItem.sap_descricao_gpm;
    delete formatedItem.descricao_gpm;

    try {
      const response = await customersAuthorizationApi.post(
        `${EndpointsMvp.ADD_ITEM(cartId?.cartId)}`,
        {
          ...bodyRequest,
          cartItem: { ...formatedItem, quote_id: cartId?.cartId }
        },
        { headers, params }
      );

      if (response.data?.error) {
        if (response.data.error?.message?.includes('No such entity')) {
          handleUpdateQuantity(0);
        }
        UnexpectedError(response.data.error);
      }

      if (quantity && quantity >= 0) {
        handleUpdateQuantity(cartQuantity + 1);
      } else {
        const cartQuantity = await getCartItemQuantity();
        handleUpdateQuantity(cartQuantity);
      }

      return response.data;
    } catch (error) {
      console.error(error);
      handleUpdateQuantity(0);
      deleteCookie('@cart');
    }
  };

  const addBulkCartItem = async ({
    items,
    withPrice = true,
    withOptions = true,
    noCheckPrice = false,
    complete = false
  }: {
    items: NewCartItem[];
    withPrice?: boolean;
    withOptions?: boolean;
    noCheckPrice?: boolean;
    complete?: boolean;
  }) => {
    if (!items) {
      return false;
    }
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);

    let cartId = getCartId();

    if (!cartId) {
      const newCartId = await createNewCart();
      cartId = String(newCartId?.cartId ?? '');
    }

    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }

    const params = {
      includeOptions: withOptions,
      includePrices: withPrice
    };

    const newItems = [...items]
      .map((item) => ({
        ...(item.item_id && { item_id: item.item_id }),
        sku: item.sku,
        qty: item.qty,
        quote_id: cartId,
        extension_attributes: item.extension_attributes
      }))
      .slice(0, 30);

    try {
      const response = await customersAuthorizationApi.post(
        `${EndpointsMvp.ADD_ITEM_BULK(cartId)}`,
        { ...bodyRequest, items: [...newItems] },
        { headers, params }
      );

      if (
        !response ||
        !response.data ||
        !response.data.items ||
        response.data?.error
      ) {
        UnexpectedError(response.data.error);
      }

      if (!noCheckPrice) {
        const productsWithPrice =
          (response &&
            response.data &&
            response.data.items &&
            response.data.items.filter(
              (product: NewCartItem) =>
                Number(product?.price?.creditPrice ?? 0) > 0
            )) ??
          [];

        handleUpdateQuantity(productsWithPrice.length ?? 0);
      }

      if (complete) {
        return response.data;
      }

      return response.data.items;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: `Falha ao atualizar itens no carrinho`,
          durationInMS: 3000
        })
      );
    }
  };

  const getCartId = (customerId = '') => {
    const cartId = getValueCookie(CookiesKey.CART_ID);
    const emitterId = getValueCookie(CookiesKey.EMITTER_ID);

    if (
      emitterId &&
      cartId &&
      (shoppingCart.emitterId === emitterId ||
        (customerId && customerId === emitterId))
    ) {
      return cartId;
    }
    return '';
  };

  const getCartIdOrGenerateOne = async () => {
    const cartId = getCartId();
    if (!cartId || !cartId.length) {
      return createNewCart();
    }
    return cartId ?? '';
  };

  const getMinimunOrder = async (): Promise<
    { items?: MinimunOrderItemProps[] } | undefined
  > => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);
    const cartId = getCartId();
    const customer = customersState.customers.find(
      (customer) => customer.customerId === shoppingCart.emitterId
    ) as customerTypeMvpProps;
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }
    try {
      const response = await customersAuthorizationApi.get(
        `${EndpointsMvp.MINIMUN_ORDER(cartId)}`,
        {
          headers: { ...headers, businessType: customer.customerType ?? '' },
          params: bodyRequest
        }
      );

      if (response.data && response.data?.message) {
        throw new Error();
      }

      return response.data;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar dados de pedido minimo',
          durationInMS: 3000
        })
      );

      return undefined;
    }
  };

  const getCart = async (): Promise<CartProps | undefined> => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);
    const cartId = getCartId();
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }
    try {
      const response = await customersAuthorizationApi.get(
        `${EndpointsMvp.CART(cartId)}`,
        { headers, params: bodyRequest }
      );

      if (response.data?.error) {
        UnexpectedError(response.data.error);
      }

      return response.data;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar pedido',
          durationInMS: 3000
        })
      );

      return undefined;
    }
  };

  const getCartItemQuantity = async ({
    alreadyItems = undefined,
    countType = false,
    paramsOptions = {
      withOptions: true,
      withPrice: true
    }
  }: {
    alreadyItems?: NewCartItem[];
    countType?: boolean;
    paramsOptions?: ParamsOptionsProps;
  } = {}): Promise<number> => {
    const params = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId,
      includeOptions: paramsOptions?.withOptions ?? true,
      includePrices: paramsOptions?.withPrice ?? true
    };
    const hasBodyKeyEmpty = Object.values(params).some((item) => !item);
    const cartId = getCartId();
    if (!cartId || hasBodyKeyEmpty) {
      return 0;
    }
    if (alreadyItems) {
      const products = alreadyItems as NewCartItem[];

      const productsWithPrice = products.filter(
        (product) => Number(product.price.creditPrice) > 0
      );

      const cartQuantity = productsWithPrice.length ?? 0;
      return cartQuantity;
    }
    try {
      if (countType) {
        const response = await customersAuthorizationApi.get(
          `${EndpointsMvp.CART_ITEM_QUANTITY(cartId)}`,
          {
            headers,
            params: {
              soldTo: shoppingCart.emitterId,
              shipTo: shoppingCart.receiverId
            }
          }
        );

        if (response.data && response.data.count) {
          return response.data.count;
        }

        return 0;
      } else {
        const response = await customersAuthorizationApi.get(
          `${EndpointsMvp.GET_ITEMS(cartId)}`,
          { headers, params }
        );

        if (response.data && response.data.length > 0) {
          const products = response.data as NewCartItem[];

          const productsWithPrice = products.filter(
            (product) => Number(product.price.creditPrice) > 0
          );
          return productsWithPrice.length ?? 0;
        }

        return 0;
      }
    } catch (error) {
      //Create a new cart when has error on get quantity
      await createNewCart('', '', true);

      return 0;
    }
  };

  const getCartItems = async (): Promise<NewCartItem[] | undefined> => {
    const params = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId,
      includePrices: true,
      includeOptions: true
    };
    const hasBodyKeyEmpty = Object.values(params).some((item) => !item);
    const cartId = getCartId();
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }
    try {
      const response = await customersAuthorizationApi.get(
        `${EndpointsMvp.GET_ITEMS(cartId)}`,
        { headers, params }
      );

      if (response.data?.error) {
        UnexpectedError(response.data.error);
      }

      return response.data;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  };

  const getCartProductsInformation = async (
    products: string[]
  ): Promise<SearchResponse[] | undefined> => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);
    const cartId = getCartId();
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }
    try {
      const response = await customersAuthorizationApi.get(
        `${EndpointsMvp.SEARCH_PRODUCTS(products)}`,
        { headers, params: bodyRequest }
      );

      if (response.data && Array.isArray(response.data)) {
        updateProductsInformation(response.data);
      }

      return response.data;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar informações dos produtos',
          durationInMS: 3000
        })
      );
    }
  };

  const deleteAllItems = async ({
    updateQty = true
  }: {
    updateQty?: boolean;
  }) => {
    const cartId = getCartId();
    try {
      const response = await customersAuthorizationApi.delete(
        `${EndpointsMvp.DELETE_ITEMS(cartId)}`,
        { headers }
      );

      if (updateQty) {
        handleUpdateQuantity(0);
      }

      return response.data as boolean;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar itens do pedido',
          durationInMS: 3000
        })
      );
    }
  };

  const deleteItem = async (itemId: number) => {
    const cartId = getCartId();
    try {
      const response = await customersAuthorizationApi.delete(
        `${EndpointsMvp.DELETE_ITEM(cartId, itemId)}`,
        { headers }
      );

      if (response.status === 200) {
        handleUpdateQuantity(cartQuantity - 1);

        return response.data as boolean;
      }

      throw new Error();
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao deletar item do pedido',
          durationInMS: 3000
        })
      );
    }
  };

  return {
    handleUpdateQuantity,
    getCartId,
    getCartIdOrGenerateOne,
    createNewCart,
    getMinimunOrder,
    addCartItem,
    addBulkCartItem,
    getCart,
    getCartItems,
    getCartItemQuantity,
    getCartProductsInformation,
    deleteItem,
    deleteAllItems
  };
};
